import DateFnsUtils from '@date-io/date-fns';
import {
    Button,
    Checkbox,
    FormControl,
    Grid,
    Input,
    InputLabel,
    ListItemText,
    MenuItem,
    Paper,
    Select
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import axios from 'axios';
import es from 'date-fns/locale/es';
import subMonths from 'date-fns/subMonths';
import React from 'react';
import constants from '../../constants';
import { useAuth } from "../../context/auth";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const useStyles = makeStyles(theme => ({
    paperContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-around',
        padding: 8,
        minHeight: '8vh',
    },
    formControl: {
        margin: 4,
        width: '100%'
    },
    dateInputLabel: { marginRight: 16, fontSize: "13px", color: 'rgba(0, 0, 0, 0.54)', width: '100%' }
}));

const listStatus = [
    constants.reportStatus.ATTENDED,
    constants.reportStatus.DISCARDED,
    constants.reportStatus.PENDING
];

// Estado a nivel de módulo para conservar los valores por defecto (todas las
// subcategorías / todos los sectores) igual que en el filtro principal.
let allSubcategories = [];
let allSectores = [];

const FiltrosDetalle = React.memo((props) => {
    const { auth } = useAuth();
    const classes = useStyles();

    const incomingFilter = props.incomingFilter || null;

    const [sectores, setSectores] = React.useState([]);
    const [subcategories, setSubcategories] = React.useState([]);
    const [startDate, setStartDate] = React.useState(
        incomingFilter && incomingFilter.startDate ? new Date(incomingFilter.startDate) : subMonths(new Date(), 3)
    );
    const [endDate, setEndDate] = React.useState(
        incomingFilter && incomingFilter.endDate ? new Date(incomingFilter.endDate) : new Date()
    );
    const [selSubcategories, setSelSubcategories] = React.useState([]);
    const [selSector, setSelSector] = React.useState([]);

    const headers = {
        'Content-Type': 'application/json',
        'Authorization': auth.token
    }

    function onDateStartChange(date) {
        setStartDate(date);
    }

    function onDateEndChange(date) {
        setEndDate(date);
    }

    function handleChangeSelSubcategories(event) {
        if (event.target.value != null) {
            setSelSubcategories(event.target.value);
        }
    }

    const handleChangeSelSector = (event) => {
        if (event.target.value != null) {
            setSelSector(event.target.value);
        }
    };

    function requestExportExcel(filter) {
        return axios.post(constants.apiBase + "web/reports/exports-excel", filter, {
            headers,
            responseType: 'blob'
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'report.xls');
            document.body.appendChild(link);
            link.click();
        }).catch(e => {
            console.error(e);
            return [];
        });
    }

    function requestZoneStatistics(filter) {
        return axios.post(constants.apiBase + "web/sectors/sectorCategoryStats", filter, {
            headers
        }).then(result => {
            return result.data.data;
        }).catch(e => {
            return [];
        });
    }

    function requestCatIncidences() {
        return axios.get(constants.apiBase + "web/categories/listForFilter", {
            headers
        }).then(result => {
            return result.data.data;
        }).catch(e => {
            console.error(e);
            return [];
        });
    }

    function requestSectores() {
        return axios.get(constants.apiBase + "web/sectors", {
            headers
        }).then(result => {
            return result.data.data;
        }).catch(e => {
            console.error(e);
            return [];
        });
    }

    // Construye el filtro base (fechas + sectores + todos los estados).
    function buildBaseFilter() {
        return {
            startDate: startDate.getTime(),
            endDate: endDate.getTime(),
            status: listStatus.map(item => item.value),
            sectors: selSector.length > 0
                ? selSector.map(item => (item != null ? item.id : null))
                : allSectores.map(item => (item ? item.id : null))
        };
    }

    // El backend agrega por categoría, por lo que para obtener el detalle por
    // subcategoría se consulta una vez por cada subcategoría (filtrando
    // 'incidences' a un solo id). Cada respuesta se etiqueta con su subcategoría.
    function runQuery(subs, baseFilter) {
        Promise.all(
            subs.map(sub =>
                requestZoneStatistics({ ...baseFilter, incidences: [sub.id] })
                    .then(rows => rows.map(row => ({
                        subcategory: sub.title,
                        sector: row.sector,
                        total: row.total
                    })))
            )
        ).then(results => {
            props.setStatsData(results.flat());
        });
    }

    function executeFilters() {
        const subs = selSubcategories.length > 0 ? selSubcategories : allSubcategories;
        runQuery(subs, buildBaseFilter());
    }

    function executeExcel() {
        const subs = selSubcategories.length > 0 ? selSubcategories : allSubcategories;
        const filter = { ...buildBaseFilter(), incidences: subs.map(sub => sub.id) };
        requestExportExcel(filter);
    }

    const renderSubcategoryOptions = () => {
        return subcategories.map(sub =>
            <MenuItem key={'sub' + sub.id} value={sub}>
                <Checkbox checked={selSubcategories.indexOf(sub) > -1} />
                <ListItemText primary={sub.title} />
            </MenuItem>
        );
    };

    const renderSectorOptions = () => {
        return sectores.map(sector =>
            <MenuItem key={'sector' + sector.id} value={sector}>
                <Checkbox checked={selSector.indexOf(sector) > -1} />
                <ListItemText primary={sector.name} />
            </MenuItem>
        );
    };

    React.useEffect(() => {
        Promise.all([requestCatIncidences(), requestSectores()]).then(([cats, secs]) => {
            const cat = cats.find(item => item.title === props.category);
            const subs = cat && cat.incidences ? cat.incidences : [];
            allSubcategories = subs;
            setSubcategories(subs);
            setSelSubcategories([]);

            setSectores(secs);
            allSectores = secs;

            let preSelectedSectors = [];
            if (incomingFilter && incomingFilter.sectors) {
                preSelectedSectors = secs.filter(sector => incomingFilter.sectors.includes(sector.id));
                setSelSector(preSelectedSectors);
            }

            const initialFilter = {
                startDate: incomingFilter && incomingFilter.startDate ? incomingFilter.startDate : startDate.getTime(),
                endDate: incomingFilter && incomingFilter.endDate ? incomingFilter.endDate : endDate.getTime(),
                status: listStatus.map(item => item.value),
                sectors: incomingFilter && incomingFilter.sectors ? incomingFilter.sectors : secs.map(sector => sector.id)
            };
            runQuery(subs, initialFilter);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <React.Fragment>
            <Grid item xs={12} style={{ paddingBottom: 0 }}>
                <Paper className={classes.paperContainer}>
                    <Grid item xs={12} md={9} style={{ paddingBottom: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={es}>
                            <Grid item xs={12} md={2}>
                                <FormControl className={classes.formControl}>
                                    <span className={classes.dateInputLabel}>Fecha de inicio</span>
                                    <DateTimePicker inputProps={{ style: { padding: '6px 12px' } }} className={classes.formControl} variant="inline" inputVariant="outlined" value={startDate} onChange={onDateStartChange} format="dd/MM/yyyy HH:mm" />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormControl className={classes.formControl}>
                                    <span className={classes.dateInputLabel}>Fecha de fin</span>
                                    <DateTimePicker inputProps={{ style: { padding: '6px 12px' } }} className={classes.formControl} variant="inline" inputVariant="outlined" value={endDate} onChange={onDateEndChange} format="dd/MM/yyyy HH:mm" />
                                </FormControl>
                            </Grid>
                        </MuiPickersUtilsProvider>
                        <Grid item xs={12} md={3}>
                            <FormControl className={classes.formControl}>
                                <InputLabel id="lblSubcategoria">Sub Categoría</InputLabel>
                                <Select
                                    labelId="lblSubcategoria"
                                    id="selSubcategoria"
                                    multiple
                                    displayEmpty
                                    value={selSubcategories}
                                    onChange={handleChangeSelSubcategories}
                                    input={<Input />}
                                    renderValue={selected => selected.length === 0
                                        ? 'SELECCIONE UNA SUBCATEGORÍA'
                                        : selected.map(item => item ? item.title : null).filter(text => text).join(', ')}
                                    MenuProps={MenuProps}>
                                    {renderSubcategoryOptions()}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl className={classes.formControl}>
                                <InputLabel id="lblSector">Sector</InputLabel>
                                <Select
                                    labelId="lblSector"
                                    id="selSector"
                                    multiple
                                    value={selSector}
                                    onChange={handleChangeSelSector}
                                    input={<Input />}
                                    renderValue={selected => selected.map(item => item ? (item.name || item.title) : null)
                                        .filter(text => text)
                                        .join(', ')}
                                    MenuProps={MenuProps}>
                                    {renderSectorOptions()}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Grid item xs={5} md={1}>
                        <Button onClick={executeFilters} style={{ width: '100%', fontSize: 12 }} variant="contained" color="primary">
                            Filtrar
                        </Button>
                    </Grid>
                    <Grid mb={4} item xs={5} md={2} xl={1}>
                        <Button onClick={executeExcel} style={{ width: '100%', fontSize: 12 }} variant="contained">
                            Exportar Excel
                        </Button>
                    </Grid>
                </Paper>
            </Grid>
        </React.Fragment>
    );
});

export default FiltrosDetalle;
